import React, { useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { CombinedStates } from 'core/types';
import { Button } from 'antd';
import { LeftOutlined, LoadingOutlined } from '@ant-design/icons';
import Stepper from 'components/Form/Stepper';
import { BackArrowIcon, NextArrowIcon } from 'components/Elements/CustomSVGIcon';
import LoadingOverlay from 'components/Loading/LoadingOverlay';
import useIsGuest from 'core/guestBooking/useIsGuest';
import { buildMainToolbar } from 'core/utils/mainToolbar';

import { LayoutProps, RequestFormNavigationState } from './types';
import Main from '../Main';

import './requestFormLayout.scss';

const RequestFormLayout: React.FC<LayoutProps> = ({ children, currentStep = 1, onBackClick, onNextClick, isBackEnabled, isNextEnabled, nextText, stepperLabels: customStepperLabels, formTitle, reduxSliceKey = 'visitRequestForm' }) => {
    const { state: navigationState } = useLocation();
    const navigate = useNavigate();
    // `navigationState` is threaded verbatim into the step handlers, so keep it
    // loosely typed there and read our own fields through a typed view.
    const formNavState = navigationState as RequestFormNavigationState | null;
    // Return the user to wherever they launched the form from (e.g. a site
    // details page), falling back to the dashboard for entry points that don't
    // provide an origin.
    const backTo = formNavState?.backTo ?? '/dashboard';

    // The subheader Back exits the whole form, regardless of which step the user
    // is on, rather than stepping through the form (which would leave stale form
    // entries behind that the origin page's own back control could return to).
    //
    // We pop straight back to the origin entry using history indices:
    // `navigate(originIdx - currentIdx)` is history.go(delta), which moves by an
    // exact number of entries. Because both indices are read live from
    // history.state.idx, the delta is correct no matter how the intervening
    // steps navigated — push grows the index, replace leaves it unchanged, and
    // either way `go(delta)` lands precisely on the origin. The only invariant
    // required is that entering the form was a push (so originIdx < currentIdx),
    // which holds for every launch point (see SiteInfo). When the index isn't
    // available (e.g. the form was opened directly / refreshed) we fall back to
    // navigating to the resolved origin.
    const handleSubHeaderBack = () => {
        const currentIdx = (window.history.state as { idx?: number } | null)?.idx;
        const { originIdx } = formNavState ?? {};
        if (typeof currentIdx === 'number' && typeof originIdx === 'number' && originIdx < currentIdx) {
            navigate(originIdx - currentIdx);
        } else {
            navigate(backTo, { replace: true });
        }
    };

    const sliceState = useSelector((state: CombinedStates) => state[reduxSliceKey]);
    const { isUpdating, isFetching, visitRequest } = sliceState;
    const { userData } = useSelector((state: CombinedStates) => state.user);

    // Guest (token) bookings render this layout with no authenticated user, so
    // the admin toolbar/nav must be suppressed.
    const isGuest = useIsGuest();

    const toolbar = isGuest ? [] : buildMainToolbar(userData?.role?.accessLevel);
    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];

    // If custom stepper labels are provided (e.g. access request), use them directly
    const useCustomLabels = !!customStepperLabels;

    // If site was pre-selected (from sites list), skip site selection step in stepper
    const hasSitePreSelected = !useCustomLabels && visitRequest?.site !== null && currentStep > 1;
    const allStepperLabels = customStepperLabels || ['Site Selection', 'Select Date', 'Add Visitors', 'Site Access', 'Work Type', 'Review'];
    const stepperLabels = hasSitePreSelected ? allStepperLabels.slice(1) : allStepperLabels;
    const adjustedCurrentStep = hasSitePreSelected ? currentStep - 1 : currentStep;

    // Hide back button on step 2 when site is pre-selected (no step 1 to go back to)
    const shouldShowBackButton = useCustomLabels ? currentStep > 1 : !(hasSitePreSelected && currentStep === 2);

    const backLink = (
        <Button type="link" onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    useEffect(() => {
        // scroll to top on render
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    return (
        <Main
            className="request-form"
            title={<div>{formTitle || 'Visitor Request Form'}</div>}
            subHeaderTitle={formTitle || 'Visitor Request Form'}
            // Authenticated users get the subheader affordance to leave the
            // form. A guest gets none: every exit from a one-off booking lands
            // on the login screen, which they have no account for and no way
            // back from — their only route through is the form's own Back and
            // Next.
            subHeaderHasBackLink={!isGuest}
            subHeaderBackLinkCallback={isGuest ? undefined : handleSubHeaderBack}
            subHeaderRightAction={(
                <div className="request-header-date">
                    {moment().format('DD MMM YYYY')}
                </div>
            )}
            showFooter={false}
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            floatingHeader={false}
            footerContent={<div style={{ lineHeight: '30px', fontSize: '16px', float: 'right' }}>&copy; copyright 2020</div>}
            floatingFooter
            backLink={backLink}
            breadcrumb={navigation}>
            <div className="main-outer-content">
                <LoadingOverlay loading={isFetching} message="Loading..." />
                <Stepper currentStep={adjustedCurrentStep} labels={stepperLabels} />
                { children }
                <div className="visitor-request-buttons-wrapper">
                    {shouldShowBackButton ? (
                        <Button type="primary" ghost onClick={() => onBackClick && onBackClick(navigationState)} disabled={!isBackEnabled}>
                            <span>
                                <BackArrowIcon style={{ marginRight: '10px' }} s={isBackEnabled ? '#da0812' : '#C0D1D8'} />
                                Back
                            </span>
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Button type="primary" onClick={() => onNextClick(navigationState)} disabled={!isNextEnabled || isUpdating || isFetching}>
                        <span>
                            {nextText}
                            { !isUpdating
                                ? <NextArrowIcon style={{ marginLeft: '10px' }} s={isNextEnabled ? '#ffffff' : '#C0D1D8'} />
                                : <LoadingOutlined style={{ marginLeft: '10px', marginRight: '0px' }} /> }
                        </span>
                    </Button>
                </div>
            </div>
        </Main>
    );
};

RequestFormLayout.propTypes = {
    nextText: PropTypes.string,
};

RequestFormLayout.defaultProps = {
    nextText: 'Next',
};

export default RequestFormLayout;
